import { Capability } from "@redotech/account-common/capability";
import { isAeoAllowed } from "@redotech/redo-aeo-common/is-aeo-allowed";
import {
  anyOrderEditingActionEnabled,
  type OrderEditingConfigSource,
} from "@redotech/redo-order-editing-settings-common/order-editing-match";
import { teamCanUseAi, type TeamCanUseAiInput } from "./team";

type Toggle = { enabled?: boolean | null } | null;

type SupportInput = NonNullable<
  NonNullable<TeamCanUseAiInput["settings"]>["support"]
>;

/** The team fields capabilities read; a full team and a projected one both fit. */
export interface CapabilityTeamInput {
  /** Resolved by the caller; on the server it comes from CRDB, not the team. */
  shopperAgentEnabled?: boolean;
  settings?: {
    aeo?: Parameters<typeof isAeoAllowed>[0];
    checkoutOptimization?: Toggle;
    contextual?: Toggle;
    convertRecyclingProducts?: boolean | null;
    customerAccounts?: Toggle;
    disputes?: Toggle;
    enableNonShopifyClaims?: boolean | null;
    hsa?: Toggle;
    ims?: Toggle;
    landedCost?: Toggle;
    marketing?: Toggle;
    orderEditing?: OrderEditingConfigSource | null;
    orderTracking?: Toggle;
    outboundLabels?: { newOmsEnabled?: boolean | null; wms?: Toggle } | null;
    packageProtection?: Toggle;
    predictiveConversion?: Toggle;
    recover?: Toggle;
    recycling?: Toggle;
    redoManagedReturns?: Toggle;
    returns?: Toggle;
    reviews?: Toggle;
    supplies?: Toggle;
    support?: (SupportInput & { enabled?: boolean | null }) | null;
    tnpl?: Toggle;
    warranties?: Toggle;
    withdrawals?: Toggle;
  } | null;
}

interface CapabilityResolver {
  /** Team document paths the resolver reads, for callers that project. */
  teamFields: readonly string[];
  isEnabled: (team: CapabilityTeamInput) => boolean;
}

type CapabilitySettingsInput = NonNullable<CapabilityTeamInput["settings"]>;

/** Settings keys whose value is a plain `{ enabled }` toggle. */
type ToggleKey = {
  [K in keyof CapabilitySettingsInput]-?: NonNullable<
    CapabilitySettingsInput[K]
  > extends { enabled?: boolean | null }
    ? K
    : never;
}[keyof CapabilitySettingsInput];

function toggle(key: ToggleKey): CapabilityResolver {
  return {
    teamFields: [`settings.${key}.enabled`],
    isEnabled: (team) => !!team.settings?.[key]?.enabled,
  };
}

export const CAPABILITY_RESOLVERS = {
  ai: {
    teamFields: ["settings.support.ai", "settings.support.billing.ai"],
    isEnabled: (team) => teamCanUseAi(team),
  },
  aeo: {
    teamFields: ["settings.aeo"],
    isEnabled: (team) => isAeoAllowed(team.settings?.aeo),
  },
  checkoutOptimization: toggle("checkoutOptimization"),
  contextual: toggle("contextual"),
  customerAccounts: toggle("customerAccounts"),
  disputes: toggle("disputes"),
  emailSms: toggle("marketing"),
  hsa: toggle("hsa"),
  ims: toggle("ims"),
  landedCost: toggle("landedCost"),
  oms: {
    teamFields: ["settings.outboundLabels.newOmsEnabled"],
    isEnabled: (team) => !!team.settings?.outboundLabels?.newOmsEnabled,
  },
  orderEditing: {
    teamFields: ["settings.orderEditing"],
    isEnabled: (team) =>
      anyOrderEditingActionEnabled(team.settings?.orderEditing),
  },
  orderTracking: toggle("orderTracking"),
  packageProtection: toggle("packageProtection"),
  predictiveConversion: toggle("predictiveConversion"),
  recover: toggle("recover"),
  recycling: {
    teamFields: [
      "settings.recycling.enabled",
      "settings.convertRecyclingProducts",
      "settings.enableNonShopifyClaims",
    ],
    isEnabled: (team) =>
      (team.settings?.recycling?.enabled ??
        !!team.settings?.convertRecyclingProducts) &&
      !!team.settings?.enableNonShopifyClaims,
  },
  redoManagedReturns: toggle("redoManagedReturns"),
  returns: toggle("returns"),
  reviews: toggle("reviews"),
  supplies: toggle("supplies"),
  support: toggle("support"),
  tnpl: toggle("tnpl"),
  warranties: toggle("warranties"),
  withdrawals: toggle("withdrawals"),
  wms: {
    teamFields: ["settings.outboundLabels.wms.enabled"],
    isEnabled: (team) => !!team.settings?.outboundLabels?.wms?.enabled,
  },
} as const satisfies Record<Capability, CapabilityResolver>;

export function isCapabilityEnabled(
  team: CapabilityTeamInput,
  capability: Capability,
): boolean {
  return CAPABILITY_RESOLVERS[capability].isEnabled(team);
}

export function resolveEnabledCapabilities(
  team: CapabilityTeamInput,
  capabilities: readonly Capability[] = Capability.options,
): Capability[] {
  return capabilities.filter((capability) =>
    isCapabilityEnabled(team, capability),
  );
}

/** Union of team paths to project before resolving the given capabilities. */
export function capabilityTeamFields(
  capabilities: readonly Capability[],
): string[] {
  return [
    ...new Set(
      capabilities.flatMap(
        (capability) => CAPABILITY_RESOLVERS[capability].teamFields,
      ),
    ),
  ];
}
